/*
 * hero.js -- vyber zdroje hero videa a rucni ovladani polaroidu
 * (plan 08-07, KOMP-02).
 *
 * CO TENHLE MODUL JE A CO NENI
 *
 * NENI nositelem obsahu hero bloku. Bez skriptu je vsechno podstatne
 * videt: nadpis, texty a tlacitka jsou cisty markup a misto videa se
 * vykresli `<picture>` s posterem (`hero.php`). Modul je ENHANCEMENT --
 * pridava pohyb a rucni prepinani fotek.
 *
 * PROC SE ZDROJ VIDEA VYBIRA V JS A NE V MARKUPU
 *
 * `<source media="...">` uvnitr `<video>` neni napric prohlizeci matice
 * podpory spolehlivy (kontrakt mobilniho chovani, radek "Hero video";
 * rozhodnuti majitele 2. 9. 17:15). U `<picture>` funguje, u `<video>`
 * ne -- proto ma `<picture>` `<source media>` a `<video>` nema zadny
 * `src` do chvile, nez ho nastavi tenhle modul.
 *
 * JEDEN ELEMENT, JEDEN ZDROJ (hrozba T-08-07-03)
 *
 * V DOM je vzdy prave jeden `<video>`. Oba mozne zdroje nese v `data-`
 * atributech a modul jeden z nich zapise do `src`. Dva elementy vedle
 * sebe by na mobilnim pripojeni znamenaly dve soubezna stahovani MP4.
 * `src` se nastavuje JEDNOU pri initu a znovu VYHRADNE pri prekroceni
 * hranice 767 px (`matchMedia` udalost `change`), nikdy pri kazdem
 * resize -- pri zmene `src` prohlizec predchozi buffer zahodi, takze
 * zbytecne prepnuti = zbytecne stazene megabajty.
 *
 * PROC `getAttribute` A NE `dataset`
 *
 * Mapovani nazvu atributu na `dataset` je definovane jen pro pomlcku
 * nasledovanou MALYM PISMENEM. `data-video-16x9` a `data-video-4x5`
 * maji za pomlckou cislici, takze vysledny nazev by obsahoval pomlcku
 * a chovani se mezi enginy lisi. `getAttribute` je jednoznacny vsude.
 *
 * PREFERS-REDUCED-MOTION: `src` se nenastavi VUBEC. Neni to jen "video
 * se neprehraje" -- pri respektovani te volby nema smysl ani stahovat
 * megabajty pohybu, ktery uzivatel odmitl. Zustane poster z `<picture>`.
 *
 * ODMITNUTY `play()` (W5): promise je `.catch()`-ovana a nic dalsiho se
 * nedeje. Poster videa zustava viditelny, konzole zustava cista. Firemni
 * Chrome/Edge autoplay bezne blokuji, takze tohle NENI okrajovy stav.
 *
 * `html.hero-ready` ZNACI "modul dobehl", ne "modul bezi" -- opacne nez
 * `reveal-ready` (08-02) a stejne jako `menu-ready` (07-03). Duvod je
 * vecny: tridou se odkryvaji TECKY polaroidu, tedy ovladaci prvky. Dokud
 * nemaji obsluhu, nesmeji byt videt (kontrakt B: zadny mrtvy ovladaci
 * prvek). Kdyby trida prisla na prvnim radku, existovalo by okno, kdy
 * tecky jsou videt a klik nic nedela.
 *
 * BEZPECNOST (threat model 08-07): zadny `innerHTML`, zadny `eval`,
 * zadna interpolace vstupu. Hodnoty jdou z `data-` atributu do `src`
 * a `poster`, tedy do atributu, ktery prohlizec sam validuje jako URL.
 * Modul nesaha na `document.body.classList` -- stavy tela jsou vyhradne
 * kontrakt `state.js` a hero zadny novy nepotrebuje.
 */

/** Trida na <html>: "hero modul dobehl" (analogie `menu-ready`). */
const READY_CLASS = 'hero-ready';

/** Hranice, pod kterou se pouziva portretovy zdroj (kontrakt: 767 px). */
const MOBILE_QUERY = '(max-width: 767px)';

/** Volba uzivatele "omezit pohyb" -- video se pak vubec nespousti. */
const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Bezpecne prevede NodeList na staticke pole.
 *
 * @param {string} selector CSS selektor.
 * @param {ParentNode} [root] Koren hledani.
 * @return {Array<Element>}
 */
function all(selector, root) {
	return Array.prototype.slice.call((root || document).querySelectorAll(selector));
}

/**
 * Ktery zdroj ma element pouzit pri aktualni sirce.
 *
 * @param {HTMLVideoElement} video Prvek videa.
 * @param {MediaQueryList|null} mq Dotaz na mobilni sirku.
 * @return {string} `4x5` nebo `16x9`.
 */
function pickSource(video, mq) {
	const has4x5 = !!video.getAttribute('data-video-4x5');

	return (mq && mq.matches && has4x5) ? '4x5' : '16x9';
}

/**
 * Zapise vybrany zdroj do elementu a zkusi ho prehrat.
 *
 * Poster se nastavuje PRED `src`: kdyby se poradi otocilo, prohlizec by
 * na okamzik ukazal sirokouhly poster z markupu v portretovem boxu.
 *
 * @param {HTMLVideoElement} video Prvek videa.
 * @param {string} key `4x5` nebo `16x9`.
 * @return {void}
 */
function applySource(video, key) {
	const src = video.getAttribute('data-video-' + key);
	const poster = video.getAttribute('data-poster-' + key);

	if (!src) {
		return;
	}

	if (poster) {
		video.setAttribute('poster', poster);
	}

	video.setAttribute('src', src);
	video.load();

	const started = video.play();

	if (started && typeof started.catch === 'function') {
		// W5: odmitnuty autoplay je bezny provozni stav, ne chyba.
		// Poster zustava viditelny a konzole cista.
		started.catch(function () {});
	}
}

/**
 * Navesi vyber zdroje na jeden `<video>`.
 *
 * @param {HTMLVideoElement} video Prvek videa.
 * @param {MediaQueryList|null} mq Dotaz na mobilni sirku.
 * @return {void}
 */
function setupVideo(video, mq) {
	let current = pickSource(video, mq);

	applySource(video, current);

	if (!mq) {
		return;
	}

	const onChange = function () {
		const next = pickSource(video, mq);

		// JEN pri realnem prekroceni hranice. Bez teto podminky by kazda
		// udalost znovu prepsala `src` a zahodila nacteny buffer.
		if (next === current) {
			return;
		}

		current = next;
		applySource(video, next);
	};

	if (mq.addEventListener) {
		mq.addEventListener('change', onChange);
	} else if (mq.addListener) {
		// Safari na spodni hranici matice podpory zna jen starsi API.
		mq.addListener(onChange);
	}
}

/**
 * Oznaci aktivni tecku (a odznaci ostatni).
 *
 * @param {Array<Element>} dots Tecky.
 * @param {number} index Poradi aktivniho snimku.
 * @return {void}
 */
function markDot(dots, index) {
	dots.forEach(function (dot, i) {
		if (i === index) {
			dot.setAttribute('aria-current', 'true');
		} else {
			dot.removeAttribute('aria-current');
		}
	});
}

/**
 * Rucni ovladani jednoho polaroidu. ZADNA autorotace (spec §3) --
 * `setInterval` v tomhle modulu zamerne neni.
 *
 * @param {Element} shot Koren polaroidu.
 * @return {void}
 */
function setupPolaroid(shot) {
	const track = shot.querySelector('.hero-swipe');
	const dots = all('.hero-dot', shot);

	if (!track || !dots.length) {
		return;
	}

	const slides = all('.hero-slide', track);

	dots.forEach(function (dot, i) {
		dot.addEventListener('click', function () {
			const slide = slides[i];

			if (slide) {
				// `scrollLeft` misto `scrollTo({behavior:'smooth'})`:
				// plynuly posun je Safari 15.4+, tedy mimo matici, a
				// options objekt by na starsim enginu spadl do vyjimky.
				track.scrollLeft = slide.offsetLeft - track.offsetLeft;
			}

			markDot(dots, i);
		});
	});

	let ticking = false;

	track.addEventListener('scroll', function () {
		if (ticking) {
			return;
		}

		ticking = true;

		window.setTimeout(function () {
			ticking = false;

			const width = track.clientWidth || 1;
			const index = Math.round(track.scrollLeft / width);

			markDot(dots, Math.max(0, Math.min(dots.length - 1, index)));
		}, 80);
	}, { passive: true });
}

/**
 * Spusti hero modul: vyber zdroje videa + rucni ovladani polaroidu.
 *
 * @return {void}
 */
export function init() {
	const reduced = !!(window.matchMedia && window.matchMedia(MOTION_QUERY).matches);
	const mq = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;

	if (!reduced) {
		all('.hero-video[data-video-16x9]').forEach(function (video) {
			setupVideo(video, mq);
		});
	}

	all('.hero-shot').forEach(setupPolaroid);

	// AZ NA KONCI: tridou se odkryvaji tecky, ktere uz maji obsluhu.
	document.documentElement.classList.add(READY_CLASS);
}
